import {formatTreatmentLabels} from '@/modules/patients-therapies/functions'
import {Box, Checkbox, Collapsible, Badge, Text} from '@chakra-ui/react'
import {PatientTherapySequence} from '@cypro/engine/schema'
import {useTranslate} from '@tolgee/react'
import {useState} from 'react'
import {LuCircleCheckBig, LuTriangleAlert, LuCircle, LuChevronDown} from 'react-icons/lu'

interface OrderCollapsibleTriggerProps {
  order: PatientTherapySequence
  anyOutOfStock: boolean
  needReconstitution: boolean
  sharedBatch: boolean
  hasRestVials: boolean
  hasReturn?: boolean
}

export function OrderCollapsibleTrigger({
  order,
  anyOutOfStock,
  needReconstitution,
  sharedBatch,
  hasRestVials,
  hasReturn,
}: OrderCollapsibleTriggerProps) {
  const {t} = useTranslate('common')

  const [checked, setChecked] = useState(false)
  const {names, dosages, carrierDetails} = formatTreatmentLabels(order, t, true)
  return (
    <Box display={'flex'} gap="2" alignItems="center" width={'100%'}>
      <Box display={'flex'} gap="2" alignItems="center" height={'100%'} paddingLeft={3}>
        <Checkbox.Root checked={checked} onCheckedChange={e => setChecked(!!e.checked)}>
          <Checkbox.HiddenInput />
          <Checkbox.Control
            width="32px"
            height="32px"
            borderRadius="md"
            backgroundColor={checked ? 'green.muted' : anyOutOfStock ? 'red.muted' : 'gray.muted'}
            borderColor={'transparent'}
          >
            <Box width={'12px'} height={'12px'}>
              {checked ? (
                <LuCircleCheckBig color={'gray'} />
              ) : anyOutOfStock ? (
                <LuTriangleAlert color={'#991919'} />
              ) : (
                <LuCircle color={'gray'} />
              )}
            </Box>
          </Checkbox.Control>
        </Checkbox.Root>
      </Box>

      <Collapsible.Trigger
        display="flex"
        gap="2"
        alignItems="center"
        width={'full'}
        height={'58px'}
        justifyContent="space-between"
        paddingRight={3}
      >
        <Text fontSize="md" fontWeight="medium">
          {names.join(' / ')} {dosages.join(' / ')} {carrierDetails.join(' ')}
        </Text>
        <Box display={'flex'} gap="2" alignItems="center">
          {needReconstitution && (
            <Badge variant="subtle" size="xs">
              {t('need_reconstitution')}
            </Badge>
          )}
          {sharedBatch && (
            <Badge variant="subtle" size="xs" colorPalette={'blue'}>
              {t('shared_vial')}
            </Badge>
          )}
          {hasRestVials && (
            <Badge colorPalette="orange" size="sm" flexShrink={0}>
              {t('rest_vials_allocated')}
            </Badge>
          )}
          {hasReturn && (
            <Badge colorPalette="purple" size="sm" flexShrink={0}>
              {t('uses_pharmacy_return')}
            </Badge>
          )}
          <Collapsible.Indicator transition="transform 0.2s" _open={{transform: 'rotate(180deg)'}}>
            <LuChevronDown />
          </Collapsible.Indicator>
        </Box>
      </Collapsible.Trigger>
    </Box>
  )
}
